import React from 'react';
import PlanLimitsPanel from './PlanLimitsPanel';
import { isPayAsYouGoPlan } from '../../utils/planCatalogUtils';

/**
 * Active plan token usage — base plan and add-on pools shown separately.
 */
const TokenUsageDisplay = ({ subscription, className = '' }) => {
  const totalUsed = Number(subscription?.total_tokens_used ?? subscription?.current_usage ?? 0) || 0;
  const planLimitRaw = subscription?.usage_limit ?? subscription?.monthly_limit ?? null;
  const planLimit = planLimitRaw !== null && planLimitRaw !== undefined ? Number(planLimitRaw) || 0 : null;
  const hasRemaining =
    subscription?.remaining_tokens !== null && subscription?.remaining_tokens !== undefined;
  const totalRemaining = hasRemaining ? Math.max(0, Number(subscription.remaining_tokens) || 0) : null;
  const planName = subscription?.plan_name || 'Active plan';

  const isPayAsYouGo = isPayAsYouGoPlan(subscription?.plan_name, subscription?.plan_type);

  const fmt = (n) => {
    if (n === null || n === undefined || Number.isNaN(n)) return '—';
    return Number(n).toLocaleString('en-US');
  };

  const addonInPool =
    planLimit !== null && totalRemaining !== null ? Math.max(0, totalRemaining - planLimit) : 0;

  const baseUsed = planLimit !== null ? Math.min(totalUsed, planLimit) : totalUsed;
  const addonUsed = planLimit !== null ? Math.max(0, totalUsed - planLimit) : 0;

  const baseRemainingCalc =
    planLimit !== null && totalRemaining !== null
      ? Math.max(0, Math.min(totalRemaining, planLimit) - baseUsed)
      : totalRemaining;

  const addonRemainingCalc = Math.max(0, addonInPool - addonUsed);
  const addonCreditedEstimate = addonRemainingCalc + addonUsed;

  const totalPool =
    planLimit !== null ? planLimit + addonCreditedEstimate : totalRemaining ?? planLimit;
  const usagePct =
    totalPool && totalPool > 0 ? Math.min(100, Math.round((totalUsed / totalPool) * 100)) : 0;

  const baseUsagePct =
    planLimit && planLimit > 0 ? Math.min(100, Math.round((baseUsed / planLimit) * 100)) : 0;
  const addonUsagePct =
    addonCreditedEstimate > 0
      ? Math.min(100, Math.round((addonUsed / addonCreditedEstimate) * 100))
      : 0;

  const barTone =
    usagePct >= 90
      ? 'bg-red-500'
      : usagePct >= 75
        ? 'bg-orange-500'
        : usagePct >= 50
          ? 'bg-amber-500'
          : 'bg-emerald-500';

  if (isPayAsYouGo) {
    return (
      <div className={`token-usage-display ${className}`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Plan" value={planName} sub="Pay-as-you-go" accent="slate" />
          <StatCard label="Tokens used" value={fmt(totalUsed)} sub="this billing period" accent="blue" />
          <StatCard
            label="Remaining"
            value={hasRemaining ? fmt(totalRemaining) : 'Unlimited'}
            sub="account balance"
            accent="emerald"
          />
        </div>
        <PlanLimitsPanel subscription={subscription} className="mt-5" />
      </div>
    );
  }

  return (
    <div className={`token-usage-display ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-5 pb-4 border-b border-indigo-100/80">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500">Current plan</p>
          <p className="text-base font-bold text-slate-900">{planName}</p>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-xs text-slate-500">Combined remaining</p>
          <p className="text-lg font-bold text-emerald-700 tabular-nums">
            {totalRemaining !== null ? fmt(totalRemaining) : '—'}
            <span className="text-xs font-medium text-slate-400 ml-1">tokens</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-5">
        <section className="rounded-2xl border border-indigo-100 bg-white p-4 shadow-sm flex flex-col h-full">
          <header className="flex items-center gap-2 mb-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 text-sm font-bold">
              P
            </span>
            <div>
              <h4 className="text-sm font-bold text-slate-800">Base plan tokens</h4>
              <p className="text-xs text-slate-500">Monthly allocation from your subscription</p>
            </div>
          </header>

          <dl className="grid grid-cols-3 gap-2 text-center mb-4">
            <MetricCell label="Limit" value={fmt(planLimit)} />
            <MetricCell label="Used" value={fmt(baseUsed)} highlight="used" />
            <MetricCell label="Left" value={fmt(baseRemainingCalc)} highlight="left" />
          </dl>

          {planLimit > 0 && (
            <div className="mt-auto">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Base usage</span>
                <span className="font-semibold text-indigo-700">{baseUsagePct}%</span>
              </div>
              <div className="h-2.5 w-full rounded-full bg-indigo-50 overflow-hidden">
                <div
                  className="h-full rounded-full bg-indigo-500 transition-all duration-500"
                  style={{ width: `${baseUsagePct}%` }}
                />
              </div>
            </div>
          )}
        </section>

        <section
          className={`rounded-2xl border p-4 shadow-sm flex flex-col h-full ${
            addonCreditedEstimate > 0
              ? 'border-amber-200 bg-gradient-to-br from-amber-50/80 to-white'
              : 'border-slate-200 bg-slate-50/50'
          }`}
        >
          <header className="flex items-center gap-2 mb-4">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-800 text-sm font-bold">
              +
            </span>
            <div>
              <h4 className="text-sm font-bold text-slate-800">Add-on tokens</h4>
              <p className="text-xs text-slate-500">Purchased token packs (separate pool)</p>
            </div>
          </header>

          {addonCreditedEstimate > 0 ? (
            <>
              <dl className="grid grid-cols-3 gap-2 text-center mb-4">
                <MetricCell label="Credited" value={fmt(addonCreditedEstimate)} tone="amber" />
                <MetricCell label="Used" value={fmt(addonUsed)} highlight="used" tone="amber" />
                <MetricCell label="Left" value={fmt(addonRemainingCalc)} highlight="left" tone="amber" />
              </dl>
              <div className="mt-auto">
                <div className="flex justify-between text-xs text-amber-700/80 mb-1">
                  <span>Add-on usage</span>
                  <span className="font-semibold">{addonUsagePct}%</span>
                </div>
                <div className="h-2.5 w-full rounded-full bg-amber-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-amber-500 transition-all duration-500"
                    style={{ width: `${addonUsagePct}%` }}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6 px-2">
              <p className="text-sm font-medium text-slate-600">No add-on tokens yet</p>
              <p className="text-xs text-slate-400 mt-1 max-w-[220px]">
                Purchase a token pack from Add-ons to extend usage beyond your plan limit.
              </p>
            </div>
          )}
        </section>
      </div>

      {totalPool > 0 && (
        <section className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="text-sm font-bold text-slate-700">Combined token pool</p>
            <p className="text-sm font-semibold text-slate-600 tabular-nums">
              {fmt(totalUsed)} / {fmt(totalPool)} used ({usagePct}%)
            </p>
          </div>

          <div className="relative h-4 w-full rounded-full bg-slate-100 overflow-hidden">
            {usagePct > 0 && (
              <div
                className={`absolute left-0 top-0 h-full ${barTone} transition-all duration-700`}
                style={{ width: `${usagePct}%` }}
              />
            )}
            {addonCreditedEstimate > 0 && planLimit > 0 && totalPool > 0 && (
              <div
                className="absolute top-0 bottom-0 w-px bg-amber-600/70 z-10"
                style={{ left: `${Math.round((planLimit / totalPool) * 100)}%` }}
                title="Base / add-on boundary"
              />
            )}
          </div>

          <div className="relative mt-2 flex justify-between text-[11px] text-slate-400 tabular-nums">
            <span>0</span>
            {addonCreditedEstimate > 0 && planLimit > 0 && (
              <span
                className="absolute -translate-x-1/2 text-amber-700 font-medium"
                style={{ left: `${Math.round((planLimit / totalPool) * 100)}%` }}
              >
                {fmt(planLimit)} base
              </span>
            )}
            <span>{fmt(totalPool)}</span>
          </div>

          <ul className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-600">
            <li className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-indigo-500" />
              Base used ({fmt(baseUsed)})
            </li>
            {addonUsed > 0 && (
              <li className="inline-flex items-center gap-1.5">
                <span className="h-2.5 w-2.5 rounded-sm bg-amber-500" />
                Add-on used ({fmt(addonUsed)})
              </li>
            )}
            <li className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm bg-emerald-200" />
              Remaining ({fmt(totalRemaining)})
            </li>
          </ul>
        </section>
      )}

      <PlanLimitsPanel subscription={subscription} className="mt-5" />

      {totalRemaining !== null && totalRemaining <= 0 && (
        <Alert tone="error" message="You have run out of tokens. Upgrade your plan or buy an add-on pack to continue." />
      )}
      {totalRemaining !== null && totalRemaining > 0 && usagePct >= 80 && (
        <Alert
          tone="warn"
          message={`You have used ${usagePct}% of your combined pool. Consider an add-on or plan upgrade.`}
        />
      )}
    </div>
  );
};

function StatCard({ label, value, sub, accent }) {
  const border =
    accent === 'blue'
      ? 'border-blue-100'
      : accent === 'emerald'
        ? 'border-emerald-100'
        : 'border-slate-200';
  return (
    <div className={`rounded-2xl border ${border} bg-white px-4 py-3 shadow-sm`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-lg font-bold text-slate-800 mt-0.5 truncate">{value}</p>
      {sub && <p className="text-xs text-slate-400">{sub}</p>}
    </div>
  );
}

function MetricCell({ label, value, highlight, tone }) {
  const valueClass =
    highlight === 'used'
      ? tone === 'amber'
        ? 'text-amber-800'
        : 'text-slate-800'
      : highlight === 'left'
        ? 'text-emerald-700'
        : tone === 'amber'
          ? 'text-amber-900'
          : 'text-indigo-800';

  return (
    <div className="rounded-xl bg-slate-50/80 border border-slate-100 px-2 py-2.5">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className={`text-sm font-bold tabular-nums mt-0.5 ${valueClass}`}>{value}</dd>
    </div>
  );
}

function Alert({ tone, message }) {
  const styles =
    tone === 'error'
      ? 'bg-red-50 border-red-200 text-red-800'
      : 'bg-orange-50 border-orange-200 text-orange-800';
  return (
    <div className={`mt-4 p-3 rounded-xl border flex items-start gap-2 text-sm font-medium ${styles}`}>
      <span aria-hidden>⚠️</span>
      <span>{message}</span>
    </div>
  );
}

export default TokenUsageDisplay;
